import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createLazyIcon } from "../../lib/lazy-icons";

const Lock = createLazyIcon("Lock");

interface CadenceToggleProps {
  /** true = récurrent, false = ponctuel. */
  recurring: boolean;
  onChange: (recurring: boolean) => void;
  /** Fonctionnalité récurrente réservée SOLO : verrouille "Récurrent". */
  locked?: boolean;
}

/**
 * Segmented toggle up-front (Ponctuel / Récurrent) affiché au-dessus du
 * calendrier. Pilote l'état `recurrence` du PollCreator ; la configuration
 * détaillée (fréquence, rappels) est révélée par RecurrenceSection en dessous.
 */
export function CadenceToggle({
  recurring,
  onChange,
  locked = false,
}: CadenceToggleProps) {
  const { t } = useTranslation(["creator"]);

  const segBase =
    "flex-1 flex flex-col items-center gap-0.5 rounded-lg px-2 py-2.5 text-sm font-medium transition-all";
  const segOn =
    "bg-primary text-primary-foreground shadow-[inset_0_0_0_1px_hsl(var(--primary-foreground)/0.14)]";
  const segOff = "bg-transparent text-muted-foreground hover:text-foreground";
  const subOn = "text-[11px] font-normal text-primary-foreground/70";
  const subOff = "text-[11px] font-normal text-muted-foreground";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("creator:intent.cap_cadence")}
        </span>
        {locked && (
          <Link
            to="/pricing"
            className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80"
            data-testid="cadence-upgrade-link"
          >
            <Lock className="w-3 h-3" />
            {t("creator:recurrence.upgrade_cta")}
          </Link>
        )}
      </div>
      <div
        className="flex h-[62px] gap-1 rounded-xl border border-border bg-muted p-1"
        role="group"
        aria-label={t("creator:intent.cap_cadence")}
        data-testid="cadence-toggle"
      >
        <button
          type="button"
          aria-pressed={!recurring}
          onClick={() => onChange(false)}
          data-testid="cadence-once"
          className={`${segBase} ${!recurring ? segOn : segOff}`}
        >
          {t("creator:intent.cadence_once")}
          <span className={!recurring ? subOn : subOff}>
            {t("creator:intent.cadence_once_sub")}
          </span>
        </button>
        <button
          type="button"
          aria-pressed={recurring}
          disabled={locked}
          onClick={() => !locked && onChange(true)}
          data-testid="cadence-recurring"
          className={`${segBase} ${
            recurring ? segOn : segOff
          } ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {t("creator:intent.cadence_recurring")}
          <span className={recurring ? subOn : subOff}>
            {t("creator:intent.cadence_recurring_sub")}
          </span>
        </button>
      </div>
    </div>
  );
}

export default CadenceToggle;
